import React from 'react';

import {assertAndContinue} from 'Asserts';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';

/**
 * A hook for calculating which elements should be collapsed in a horizontal nav bar. Returns a ref for the
 * container and a map of which elements should be hidden.
 *
 * Note that using hardcoded widths isn't ideal, but automatically measuring them gets complicated quickly,
 * so this will work well enough for now.
 */
export default function useCollapsibleNavBar<TKey extends string>({
  collapsibleElements,
  dropdownWidth,
  uncollapsibleWidth = 0,
}: {
  /** The width of any elements of the nav bar which cannot be collapsed */
  uncollapsibleWidth?: number;
  /** The width of the dropdown component which will replace the collapsed elements */
  dropdownWidth: number;
  /** Each elements which should be collapsed */
  collapsibleElements: Array<{
    /** Key for the elements */
    key: TKey;
    /** Width of the element */
    width: number;
    /** Priority of showing this element. Buttons with higher priority (1 is highest) are hidden last. */
    priority: number;
  }>;
}): {
  ref: React.RefCallback<HTMLElement>;
  hiddenStatusByElementKey: {[key in TKey]?: boolean};
} {
  const {ref, width} = useResizeObserver();
  assertAndContinue(
    new Set(collapsibleElements.map((element) => element.priority)).size === collapsibleElements.length,
    'Elements should have distinct priorities.'
  );

  const hiddenStatusByElementKey: {[key in TKey]?: boolean} = {};
  // Don't hide anything until we have width.
  if (width == null) {
    return {ref, hiddenStatusByElementKey};
  }
  let widthRemaining = width - uncollapsibleWidth;
  collapsibleElements.sort((a, b) => a.priority - b.priority);
  for (const element of collapsibleElements) {
    widthRemaining -= element.width;
    // Buffer of 5 pixels plus the dropdown component
    if (widthRemaining < 5 + dropdownWidth) {
      hiddenStatusByElementKey[element.key] = true;
    }
  }
  return {ref, hiddenStatusByElementKey};
}
